import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { gsap } from '@/lib/gsap'

// A stretch of open scroll where a bank of cloud rises and clears into the ivory
// fleet scene. The clouds live in the fixed sky layer; this section only sets the pace.
const CloudRise: FC = (): ReactNode => {
    const zoneRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const zone = zoneRef.current
        if (!zone) return

        const bank = document.querySelector<HTMLElement>('[data-sky="bank"]')
        const whisper = document.querySelector<HTMLElement>('[data-sky="whisper"]')
        const veil = document.querySelector<HTMLElement>('[data-sky="veil"]')
        const mm = gsap.matchMedia()

        mm.add({
            motion: '(prefers-reduced-motion: no-preference)',
            reduce: '(prefers-reduced-motion: reduce)'
        }, (context) => {
            const { motion } = context.conditions as { motion: boolean }

            const timeline = gsap.timeline({
                defaults: { ease: 'none' },
                scrollTrigger: {
                    trigger: zone,
                    start: 'top bottom',
                    end: 'bottom bottom',
                    scrub: 1,
                    refreshPriority: 8
                }
            })

            if (motion) {
                timeline
                    .fromTo(bank, { yPercent: 105, scale: 1, autoAlpha: 1 }, { yPercent: -8, scale: 1.45, ease: 'power1.out', duration: 6 }, 0)
                    .fromTo(whisper, { autoAlpha: 0, y: 40, filter: 'blur(8px)' }, { autoAlpha: 1, y: 0, filter: 'blur(0px)', duration: 1.6 }, 2.2)
                    .to(whisper, { autoAlpha: 0, y: -30, filter: 'blur(6px)', duration: 1.2 }, 4.4)
                    .fromTo(veil, { autoAlpha: 0 }, { autoAlpha: 1, duration: 1.8 }, 4.4)
            } else {
                timeline
                    .fromTo(bank, { autoAlpha: 0, yPercent: -8, scale: 1.45 }, { autoAlpha: 1, duration: 3 }, 0)
                    .fromTo(whisper, { autoAlpha: 0 }, { autoAlpha: 1, duration: 1.4 }, 2.2)
                    .to(whisper, { autoAlpha: 0, duration: 1 }, 4.4)
                    .fromTo(veil, { autoAlpha: 0 }, { autoAlpha: 1, duration: 1.8 }, 4.4)
            }
        })

        return () => mm.revert()
    }, [])

    return <div ref={zoneRef} data-cloud-rise aria-hidden='true' className='relative z-10 h-[220vh]' />
}

export default CloudRise
